import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FiHeart, FiUsers, FiShield, FiArrowRight } from 'react-icons/fi';
import useScrollReveal from '../hooks/useScrollReveal';
import WaveDivider from '../components/WaveDivider';

const gentleShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #111f11 0%, #1a2e1a 20%, #2a3d28 40%, #3d5a3a 60%, #1a2e1a 80%, #111f11 100%);
  background-size: 400% 400%;
  animation: ${gentleShift} 25s ease infinite;
`;

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  pointer-events: none;
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};

  &::-webkit-media-controls {
    display: none !important;
  }
  &::-webkit-media-controls-enclosure {
    display: none !important;
  }
  &::-webkit-media-controls-panel {
    display: none !important;
  }
  &::-webkit-media-controls-start-playback-button {
    display: none !important;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(17, 31, 17, 0.5);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
  padding: 24px;
`;

const HeroCard = styled.div`
  background: rgba(17, 31, 17, 0.65);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 48px;
  max-width: 680px;
  margin: 0 auto;

  @media (max-width: 600px) {
    padding: 32px;
  }
`;

const HeroLabel = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--sage-light);
  margin-bottom: 16px;
`;

const HeroTitle = styled.h1`
  font-family: 'Lora', serif;
  font-size: clamp(2.5rem, 5vw, 3.8rem);
  font-weight: 600;
  color: white;
  margin-bottom: 16px;
  line-height: 1.15;
`;

const HeroTagline = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 12px;
  line-height: 1.6;
`;

const HeroLocation = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: var(--text-light);
  margin-bottom: 32px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--sage);
  color: white;
  padding: 14px 36px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.8rem;
  transition: background 0.3s ease;

  &:hover {
    background: var(--sage-muted);
  }
`;

const WelcomeSection = styled.section`
  background: var(--cream);
  padding: 100px 24px;
`;

const WelcomeInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 60px;

  @media (max-width: 900px) {
    flex-direction: column-reverse;
    gap: 40px;
    text-align: center;
  }
`;

const WelcomeText = styled.div`
  flex: 1;
`;

const WelcomeImage = styled.img`
  flex: 0 0 400px;
  max-width: 400px;
  max-height: 450px;
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(26, 46, 26, 0.08);

  @media (max-width: 900px) {
    flex: none;
    max-width: 100%;
    max-height: 350px;
  }
`;

const NatureBand = styled.div`
  height: 300px;
  background-image: url('/images/mountain-view.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(46, 51, 40, 0.25);
  }

  @media (max-width: 768px) {
    background-attachment: scroll;
  }
`;

const SectionLabel = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--sage-muted);
  margin-bottom: 16px;
`;

const SectionHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 32px;
  line-height: 1.3;
`;

const BodyText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.8;
  margin-bottom: 20px;
`;

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--sage-muted);
  margin-top: 12px;
  transition: color 0.3s ease, gap 0.3s ease;

  &:hover {
    color: var(--spruce);
    gap: 12px;
  }
`;

const ServicesSection = styled.section`
  background: var(--frost);
  padding: 100px 24px;
`;

const ServicesInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-top: 48px;
  margin-bottom: 48px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const ServiceCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 1px 4px var(--shadow);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-3px);
  }
`;

const CardIcon = styled.div`
  font-size: 2.5rem;
  color: var(--sage);
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
`;

const CardTitle = styled.h3`
  font-family: 'Lora', serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 14px;
`;

const CardText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-muted);
  line-height: 1.7;
`;

const CTASection = styled.section`
  background: var(--mist);
  padding: 100px 24px;
  text-align: center;
`;

const CTAInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTAHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 16px;
`;

const CTASubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
  margin-bottom: 32px;
`;

const Home = () => {
  useScrollReveal();
  const [videoHidden, setVideoHidden] = useState(false);

  return (
    <>
      <HeroSection>
        <HeroGradient />
        <HeroVideo
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          tabIndex={-1}
          $hidden={videoHidden}
          onError={() => setVideoHidden(true)}
        >
          <source src="/videos/hero-bg.mp4" type="video/mp4" />
        </HeroVideo>
        <HeroOverlay />
        <HeroContent>
          <HeroCard>
            <HeroLabel>Audrey Kiely, MSW, LICSW</HeroLabel>
            <HeroTitle>Blue Spruce Therapy</HeroTitle>
            <HeroTagline>Trauma-Informed, Relationship-Focused Therapy</HeroTagline>
            <HeroLocation>In person in Northern Vermont, and online in Vermont, Massachusetts, California, and Pennsylvania</HeroLocation>
            <CTAButton to="/contact">Schedule a consultation</CTAButton>
          </HeroCard>
        </HeroContent>
      </HeroSection>

      <WaveDivider topColor="#111f11" bottomColor="#eef0eb" />

      <WelcomeSection>
        <WelcomeInner>
          <WelcomeText>
            <SectionLabel className="fade-in">Welcome</SectionLabel>
            <SectionHeading className="fade-in">
              Feel Less Alone in Your Own Experience
            </SectionHeading>
            <BodyText className="fade-in delay-1">
              I come to this work with a belief that people deserve to feel safety
              and joy in their lives. I think therapy is at its best when it helps
              us feel less alone in our own experience.
            </BodyText>
            <BodyText className="fade-in delay-2">
              Maybe you've been noticing that things just feel off recently. Maybe
              you've tried therapy in the past and feel like you've hit the same
              wall every time. Maybe you've been carrying your pain for years.
              Whatever has brought you here, I want to hear about it.
            </BodyText>
            <InlineLink to="/about" className="fade-in delay-4">
              Learn more about my approach <FiArrowRight />
            </InlineLink>
          </WelcomeText>
          <WelcomeImage
            src="/images/sunlight-trees.jpg"
            alt="Birch trees with warm sunlight filtering through"
            className="fade-in delay-2"
          />
        </WelcomeInner>
      </WelcomeSection>

      <WaveDivider topColor="#eef0eb" bottomColor="#e4e8e1" />

      <ServicesSection>
        <ServicesInner>
          <SectionLabel className="fade-in">Services</SectionLabel>
          <SectionHeading className="fade-in">How I Can Help</SectionHeading>
          <CardsGrid>
            <ServiceCard className="fade-in delay-1">
              <CardIcon><FiHeart /></CardIcon>
              <CardTitle>Relationship Therapy</CardTitle>
              <CardText>
                For couples, families, polycules, and all relationship
                configurations seeking deeper connection and understanding.
              </CardText>
            </ServiceCard>
            <ServiceCard className="fade-in delay-2">
              <CardIcon><FiUsers /></CardIcon>
              <CardTitle>Individual Therapy</CardTitle>
              <CardText>
                Trauma-informed individual therapy for adults and teenagers
                navigating grief, anxiety, transitions, and self-discovery.
              </CardText>
            </ServiceCard>
            <ServiceCard className="fade-in delay-3">
              <CardIcon><FiShield /></CardIcon>
              <CardTitle>EMDR Therapy</CardTitle>
              <CardText>
                Eye Movement Desensitization and Reprocessing as primary therapy
                or adjunct support for trauma processing.
              </CardText>
            </ServiceCard>
          </CardsGrid>
          <InlineLink to="/services" className="fade-in">
            View All Services <FiArrowRight />
          </InlineLink>
        </ServicesInner>
      </ServicesSection>

      <WaveDivider topColor="#e4e8e1" bottomColor="transparent" />

      <NatureBand />

      <WaveDivider topColor="transparent" bottomColor="#d8ddd5" />

      <CTASection>
        <CTAInner className="fade-in">
          <CTAHeading>Ready to Take the First Step?</CTAHeading>
          <CTASubtitle>
            I offer a free 15-minute phone consultation so we can see if we're a
            good fit.
          </CTASubtitle>
          <CTAButton to="/contact">Book a Consultation</CTAButton>
        </CTAInner>
      </CTASection>
    </>
  );
};

export default Home;
